// DetectorFilter.tsx
import React from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';

import { EventdetectorDefinition } from '@ais-ui/api-spec/eventdetector';
import { GroundingConfigurationSchema } from '@ais-ui/api-spec/grounding';
import { useProfile } from '@ais-ui/profile';
import { DetectorFilters } from './types';
import { FilterTrigger } from '../../../components/FilterTrigger';

interface DetectorFilterProps {
  detectorFilters: DetectorFilters;
  setDetectorFilters: React.Dispatch<React.SetStateAction<DetectorFilters>>;
  detectors: EventdetectorDefinition[];
  groundingDetectors: GroundingConfigurationSchema[];
}

// Extract unique detector types
const getDetectorTypeOptions = (
  detectors: EventdetectorDefinition[],
  groundingDetectors: GroundingConfigurationSchema[]
) => {
  const combinedDetectors = [...detectors, ...groundingDetectors];

  if (!combinedDetectors || combinedDetectors.length === 0) {
    return [];
  }

  const typeSet = new Set<string>();

  combinedDetectors.forEach((detector) => {
    const detectorType = detector.type;
    if (detectorType && detectorType !== 'unknown') {
      typeSet.add(detectorType.toLowerCase());
    }
  });

  return Array.from(typeSet)
    .map((type) => ({
      value: type,
      label: type.charAt(0).toUpperCase() + type.slice(1),
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

// Helper to safely get owner from either detector type
const getDetectorOwner = (
  detector: EventdetectorDefinition | GroundingConfigurationSchema
): string | undefined => {
  if ('createdBy' in detector) {
    // GroundingConfigurationSchema only has createdBy, not owner
    return detector.createdBy || undefined;
  }
  // EventdetectorDefinition has owner
  return (detector as EventdetectorDefinition).owner;
};

// Extract unique owners
const getOwnerOptions = (
  detectors: EventdetectorDefinition[],
  groundingDetectors: GroundingConfigurationSchema[],
  currentUsername: string | undefined
) => {
  const combinedDetectors = [...detectors, ...groundingDetectors];

  if (!combinedDetectors || combinedDetectors.length === 0) {
    return [];
  }

  const ownerSet = new Set<string>();

  combinedDetectors.forEach((detector) => {
    const owner = getDetectorOwner(detector);
    if (owner) {
      ownerSet.add(owner);
    }
  });

  const uniqueOwners = Array.from(ownerSet);

  // Separate current user from other owners
  const isCurrentUser = (owner: string) => owner === currentUsername;
  const currentUserOwner = uniqueOwners.find(isCurrentUser);
  const otherOwners = uniqueOwners
    .filter((owner) => !isCurrentUser(owner))
    .sort((a, b) => a.localeCompare(b));

  // Build the final list with current user first (as "Me")
  const result: Array<{ value: string; label: string }> = [];

  if (currentUserOwner) {
    result.push({ value: currentUserOwner, label: 'Me' });
  }

  otherOwners.forEach((owner) => {
    result.push({ value: owner, label: owner });
  });

  return result;
};

export const DetectorFilter = ({
  detectorFilters,
  setDetectorFilters,
  detectors,
  groundingDetectors,
}: DetectorFilterProps) => {
  const { username } = useProfile();
  const detectorTypeOptions = getDetectorTypeOptions(
    detectors,
    groundingDetectors
  );
  const ownerOptions = getOwnerOptions(detectors, groundingDetectors, username);

  // Count active filters
  const getActiveFiltersCount = () => {
    let count = 0;
    if (detectorFilters.owner !== 'all') count++;
    if (detectorFilters.type !== 'all') count++;
    if (detectorFilters.severity !== 'all') count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <DropdownMenu>
      <Tooltip>
        <DropdownMenuTrigger asChild>
          <TooltipTrigger asChild>
            <FilterTrigger activeFilterCount={activeFiltersCount} />
          </TooltipTrigger>
        </DropdownMenuTrigger>
        <TooltipContent>
          {activeFiltersCount > 0
            ? `Filter detectors (${activeFiltersCount} active)`
            : 'Filter detectors'}
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" className="w-64 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div className="font-semibold text-sm">Filter Detectors</div>
          {activeFiltersCount > 0 && (
            <Badge variant="secondary" className="text-xs">
              {activeFiltersCount} active
            </Badge>
          )}
        </div>

        {/* Owner Filter */}
        {ownerOptions.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Owner</div>
            <Select
              value={detectorFilters.owner}
              onValueChange={(value) =>
                setDetectorFilters((prev) => ({
                  ...prev,
                  owner: value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="All Owners" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Owners</SelectItem>
                {ownerOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Detector Type Filter */}
        {detectorTypeOptions.length > 0 && (
          <div className="space-y-2">
            <div className="text-sm font-medium">Detector Type</div>
            <Select
              value={detectorFilters.type}
              onValueChange={(value) =>
                setDetectorFilters((prev) => ({
                  ...prev,
                  type: value,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {detectorTypeOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Severity Filter */}
        <div className="space-y-2">
          <div className="text-sm font-medium">Severity</div>
          <Select
            value={detectorFilters.severity}
            onValueChange={(value) =>
              setDetectorFilters((prev) => ({
                ...prev,
                severity: value as DetectorFilters['severity'],
              }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="note">Note</SelectItem>
              <SelectItem value="warning">Warning</SelectItem>
              <SelectItem value="alert">Alert</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Clear Filters Button */}
        <div className="pt-2 border-t">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() =>
              setDetectorFilters({
                owner: 'all',
                type: 'all',
                severity: 'all',
              })
            }
            disabled={activeFiltersCount === 0}
          >
            Clear Filters
          </Button>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
